import { useMutation } from '@apollo/client';
import type { FC } from 'react';

import { UploadApplication } from '@xing-com/crate-common-graphql-types';
import { usePageContext } from '@xing-com/crate-entity-pages-entity-pages-shared-context-entity-page-context';
import type { VideoFormCreateProps } from '@xing-com/crate-video-upload';
import { VideoFormCreate } from '@xing-com/crate-video-upload';

import { CreateVideoDocument } from '../../graphql/mutations/create-video-mutation.gql-types';
import { DeleteVideoDocument } from '../../graphql/mutations/delete-video-mutation.gql-types';
import type { UpdateVideoMutationVariables } from '../../graphql/mutations/update-video-mutation.gql-types';
import { UpdateVideoDocument } from '../../graphql/mutations/update-video-mutation.gql-types';

type Props = Omit<
  VideoFormCreateProps,
  | 'createVideoMutation'
  | 'updateVideoMutation'
  | 'deleteVideoMutation'
  | 'application'
>;
export const EntityPageVideoCreate: FC<Props> = (props) => {
  const { pageContext } = usePageContext();
  const { pageId, globalId } = pageContext ?? {};

  const [createVideo] = useMutation(CreateVideoDocument);
  const [updateVideo] = useMutation(UpdateVideoDocument);
  const [deleteVideo] = useMutation(DeleteVideoDocument);

  const handleCreateVideo = async (
    fileName: string
  ): Promise<{
    uploadUrl: string;
    videoId: string;
    mediaId: string;
  }> => {
    const { data, errors } = await createVideo({
      variables: {
        fileName: fileName,
        ownerUrn: globalId,
        pageId: pageId,
      },
    });
    const error = data?.entityPageCreateVideoMedia?.error || errors;

    if (error) {
      throw error;
    }

    const successData = data?.entityPageCreateVideoMedia?.success;

    if (!successData) {
      throw new Error('No success data returned from createVideo mutation');
    }

    const uploadUrl = successData.videoDetails?.links.uploadUrl;
    const videoId =
      successData.videoDetails?.id ?? successData.videoDetails?.ref;
    const mediaId = successData.id;

    if (!uploadUrl) {
      throw new Error('No upload URL returned from createVideo mutation');
    }
    if (!videoId) {
      throw new Error('No video ID returned from createVideo mutation');
    }

    return {
      uploadUrl: uploadUrl,
      videoId: videoId,
      mediaId: mediaId,
    };
  };
  const handleUpdateVideo = async (
    mediaId: string,
    videoData: UpdateVideoMutationVariables['videoData']
  ) => {
    const { data, errors } = await updateVideo({
      variables: {
        mediaId: mediaId,
        pageId: pageId,
        videoData: videoData,
      },
    });

    const error = data?.entityPageUpdateVideoMedia?.error || errors;
    if (error) {
      throw error;
    }

    return;
  };
  const handleDeleteVideo = async (mediaId: string) => {
    const { data, errors } = await deleteVideo({
      variables: {
        mediaId: mediaId,
        pageId: pageId,
      },
    });

    const error = data?.entityPageDeleteMedia?.error || errors;
    if (error) {
      throw error;
    }

    return;
  };

  return (
    <VideoFormCreate
      application={UploadApplication.EntityPages}
      createVideoMutation={handleCreateVideo}
      updateVideoMutation={handleUpdateVideo}
      deleteVideoMutation={handleDeleteVideo}
      {...props}
    />
  );
};
